import React, { useState, useEffect } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useLocation } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    Card,
    CardContent,
    CardActions,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Alert,
    CircularProgress,
    Autocomplete,
    InputAdornment,
    Divider,
    Avatar,
    Badge,
    IconButton,
    Tooltip,
    Fade,
    Slide,
    Container,
    LinearProgress,
    FormControlLabel,
    Checkbox,
    Input,
    Menu,
    ListItemIcon,
    ListItemText,
    Backdrop
} from '@mui/material';
import {
    Search as SearchIcon,
    LocalShipping as TrackIcon,
    Add as AddIcon,
    History as HistoryIcon,
    LocationOn as LocationIcon,
    Schedule as TimeIcon,
    Person as PersonIcon,
    CheckCircle as CheckIcon,
    Circle as CircleIcon,
    Today as TodayIcon,
    PendingActions as PendingIcon,
    Done as DeliveredIcon,
    Refresh as RefreshIcon,
    Timeline as TimelineIcon,
    KeyboardArrowRight as ArrowRightIcon,
    FlightTakeoff as TransitIcon,
    Inventory as PackageIcon,
    Phone as PhoneIcon,
    Business as BusinessIcon,
    Home as HomeIcon,
    AccessTime as ClockIcon,
    CloudUpload as UploadIcon,
    Image as ImageIcon,
    VerifiedUser as SealIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    MoreVert as MoreIcon,
    Warning as WarningIcon,
    Close as CloseIcon,
    ZoomIn as ZoomInIcon,
    DirectionsCar as VehicleIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../hooks/useApiHandler';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { trackingService } from '../../services/trackingService';
import { vehicleService } from '../../services/vehicleService';
import authService from '../../services/authService';

const TrackingManagement = () => {
    const { handleApiCallWithLoading, handleApiCall, showSuccess, showError, showInfo } = useApiHandler();
    const location = useLocation();
    
    // State management
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedShipment, setSelectedShipment] = useState(null);
    const [trackingHistory, setTrackingHistory] = useState(null);
    const [activities, setActivities] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    
    // Dialog states
    const [addTrackingDialogOpen, setAddTrackingDialogOpen] = useState(false);
    const [editTrackingDialogOpen, setEditTrackingDialogOpen] = useState(false);
    const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
    const [imageViewerOpen, setImageViewerOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    
    // Edit/Delete states
    const [editingEntry, setEditingEntry] = useState(null);
    const [deleteEntry, setDeleteEntry] = useState(null);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedEntryId, setSelectedEntryId] = useState(null);
    
    // Form data
    const [newTrackingData, setNewTrackingData] = useState({
        awb_no: '',
        activity_id: '',
        activity_date: new Date().toISOString().split('T')[0],
        activity_time: new Date().toTimeString().slice(0, 5),
        activity_location: '',
        activity_remark: '',
        vehicle_id: null,
        vehicle_number: '',
        is_sealed_signed: false,
        signed_copy_image: null
    });
    
    // Image preview state
    const [imagePreview, setImagePreview] = useState(null);

    // Permission checking
    const currentUser = authService.getCurrentUser();
    const canEditTracking = currentUser?.role?.name === 'Administrator' || 
                           currentUser?.role?.slug === 'admin' ||
                           authService.hasPermission('edit-tracking') ||
                           authService.hasPermission('manage-tracking');

    useEffect(() => {
        loadTrackingActivities();
        loadVehicles();
    }, []);

    useEffect(() => {
        // Check if navigated with AWB data
        if (location.state?.awbNumber) {
            setSearchTerm(location.state.awbNumber);
            handleSearch(location.state.awbNumber);
        }
    }, [location.state]);

    const loadTrackingActivities = async () => {
        const response = await trackingService.getTrackingActivities();
        if (response.success) {
            setActivities(response.data);
        }
    };

    // Load active vehicles for the optional vehicle picker (mirrors the mobile app)
    const loadVehicles = async () => {
        try {
            const response = await vehicleService.getActiveVehicles();
            if (response.success) {
                setVehicles(response.data);
            }
        } catch (error) {
            // Vehicle selection is optional; fail silently and leave the list empty
            console.error('Error loading vehicles:', error);
        }
    };

    // Resolve the Autocomplete value from stored vehicle_id/vehicle_number.
    // Returns the matching registered vehicle object, a free-text string, or null.
    const resolveVehicleValue = (data) => {
        if (data?.vehicle_id) {
            return vehicles.find(v => v.id === data.vehicle_id) || data.vehicle_number || null;
        }
        return data?.vehicle_number || null;
    };

    // Apply an Autocomplete selection (registered vehicle object or free-text string)
    // back into a tracking-data setter.
    const applyVehicleSelection = (setter, newValue) => {
        if (newValue && typeof newValue === 'object') {
            setter(prev => ({ ...prev, vehicle_id: newValue.id, vehicle_number: newValue.vehicle_number }));
        } else {
            setter(prev => ({ ...prev, vehicle_id: null, vehicle_number: newValue || '' }));
        }
    };

    const vehicleOptionLabel = (option) => {
        if (typeof option === 'string') return option;
        if (!option) return '';
        return option.vehicle_type
            ? `${option.vehicle_number} (${option.vehicle_type})`
            : option.vehicle_number;
    };

    const handleSearch = (value) => {
        setSearchTerm(value);
    };

    // Search once the user pauses typing, and ignore a response that arrives
    // after the search text has moved on
    const debouncedSearchTerm = useDebouncedValue(searchTerm);

    useEffect(() => {
        if (debouncedSearchTerm.length < 2) {
            setSearchResults([]);
            return undefined;
        }

        let active = true;
        setSearchLoading(true);
        trackingService.searchAllActiveBookings(debouncedSearchTerm)
            .then((response) => {
                if (active) setSearchResults(response.success ? response.data : []);
            })
            .finally(() => {
                if (active) setSearchLoading(false);
            });

        return () => {
            active = false;
        };
    }, [debouncedSearchTerm]);

    const handleSelectShipment = async (shipment) => {
        setSelectedShipment(shipment);
        setLoading(true);
        
        const response = await trackingService.getTrackingHistory(shipment.awb_no);
        if (response.success) {
            setTrackingHistory(response.data);
        } else {
            showError('Failed to load tracking history');
        }
        setLoading(false);
    };

    const handleActivityChange = (activityId) => {
        setNewTrackingData(prev => ({
            ...prev,
            activity_id: activityId,
            // Auto-check seal & sign for "Delivered Successfully" (ID: 8)
            is_sealed_signed: activityId === 8 ? true : prev.is_sealed_signed
        }));
    };

    // Handle activity change for edit form
    const handleEditActivityChange = (activityId) => {
        setEditingEntry(prev => ({
            ...prev,
            activity_id: activityId,
            // Auto-check seal & sign for "Delivered Successfully" (ID: 8)
            is_sealed_signed: activityId === 8 ? true : prev.is_sealed_signed
        }));
    };

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                showError('Please select a valid image file');
                return;
            }

            // Validate file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                showError('Image size should be less than 5MB');
                return;
            }

            setNewTrackingData(prev => ({
                ...prev,
                signed_copy_image: file
            }));

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => setImagePreview(e.target.result);
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setNewTrackingData(prev => ({
            ...prev,
            signed_copy_image: null
        }));
        setImagePreview(null);
    };

    // Image upload for edit form
    const handleEditImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                showError('Please select a valid image file');
                return;
            }

            // Validate file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                showError('Image size should be less than 5MB');
                return;
            }

            setEditingEntry(prev => ({
                ...prev,
                signed_copy_image: file
            }));

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => setImagePreview(e.target.result);
            reader.readAsDataURL(file);
        }
    };

    const removeEditImage = () => {
        setEditingEntry(prev => ({
            ...prev,
            signed_copy_image: null
        }));
        setImagePreview(null);
    };

    const handleAddTrackingEntry = async () => {
        // Create FormData for file upload
        const formData = new FormData();
        
        // Add basic fields
        formData.append('awb_no', newTrackingData.awb_no);
        formData.append('activity_id', newTrackingData.activity_id);
        formData.append('activity_date', newTrackingData.activity_date);
        formData.append('activity_time', newTrackingData.activity_time);
        formData.append('activity_location', newTrackingData.activity_location);
        
        // Add optional remark if provided
        if (newTrackingData.activity_remark) {
            formData.append('activity_remark', newTrackingData.activity_remark);
        }

        // Add optional vehicle (registered vehicle id and/or a typed number)
        if (newTrackingData.vehicle_id) {
            formData.append('vehicle_id', newTrackingData.vehicle_id);
        }
        if (newTrackingData.vehicle_number) {
            formData.append('vehicle_number', newTrackingData.vehicle_number);
        }

        // Only add delivery confirmation fields for activity ID 8
        if (newTrackingData.activity_id == 8) {
            formData.append('is_sealed_signed', newTrackingData.is_sealed_signed ? '1' : '0');
            
            // Only add image if one is selected
            if (newTrackingData.signed_copy_image) {
                formData.append('signed_copy_image', newTrackingData.signed_copy_image);
            }
        }

        const response = await trackingService.addTrackingEntry(formData);
        if (response.success) {
            showSuccess('Tracking entry added successfully');
            setAddTrackingDialogOpen(false);
            setNewTrackingData({
                awb_no: '',
                activity_id: '',
                activity_date: new Date().toISOString().split('T')[0],
                activity_time: new Date().toTimeString().slice(0, 5),
                activity_location: '',
                activity_remark: '',
                vehicle_id: null,
                vehicle_number: '',
                is_sealed_signed: false,
                signed_copy_image: null
            });
            setImagePreview(null);

            // Refresh tracking history if this shipment is selected
            if (selectedShipment && selectedShipment.awb_no === newTrackingData.awb_no) {
                handleSelectShipment(selectedShipment);
            }
        } else {
            if (response.errors) {
                const errorMessages = Object.values(response.errors).flat();
                showError(errorMessages.join(', '));
            } else {
                showError(response.message || 'Failed to add tracking entry');
            }
        }
    };

    // Menu handlers
    const handleMenuOpen = (event, entryId) => {
        setMenuAnchor(event.currentTarget);
        setSelectedEntryId(entryId);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setSelectedEntryId(null);
    };

    // Edit tracking entry
    const handleEditEntry = (entry) => {
        // Find the activity ID by matching the activity name
        const matchingActivity = activities.find(activity => activity.name === entry.activity);
        
        // Clear any existing image preview
        setImagePreview(null);
        
        // Convert date string to YYYY-MM-DD format without timezone issues
        const formatDateForInput = (dateString) => {
            if (!dateString) return new Date().toISOString().split('T')[0];
            
            // If the date is already in YYYY-MM-DD format, return as is
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
                return dateString;
            }
            
            // Parse the date and format it properly
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return new Date().toISOString().split('T')[0];
            }
            
            // Use local date components to avoid timezone issues
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            
            return `${year}-${month}-${day}`;
        };
        
        setEditingEntry({
            id: entry.id,
            activity_id: matchingActivity ? matchingActivity.id : '',
            activity_date: formatDateForInput(entry.date),
            activity_time: entry.time ? convertTo24Hour(entry.time) : new Date().toTimeString().slice(0, 5),
            activity_location: entry.location || '',
            activity_remark: entry.remark || '',
            vehicle_id: entry.vehicle_id || null,
            vehicle_number: entry.vehicle_number || '',
            is_sealed_signed: entry.is_sealed_signed || false,
            signed_copy_image: null
        });
        setEditTrackingDialogOpen(true);
        handleMenuClose();
    };

    // Convert 12-hour to 24-hour format
    const convertTo24Hour = (time12h) => {
        if (!time12h || !time12h.includes(' ')) {
            // If already in 24-hour format or invalid, return as is
            return time12h || new Date().toTimeString().slice(0, 5);
        }
        
        const [time, modifier] = time12h.split(' ');
        let [hours, minutes] = time.split(':');
        
        hours = parseInt(hours, 10);
        
        if (modifier === 'AM') {
            if (hours === 12) {
                hours = 0;
            }
        } else if (modifier === 'PM') {
            if (hours !== 12) {
                hours += 12;
            }
        }
        
        // Ensure two-digit format
        const formattedHours = hours.toString().padStart(2, '0');
        const formattedMinutes = minutes.padStart(2, '0');
        
        return `${formattedHours}:${formattedMinutes}`;
    };

    // Update tracking entry
    const handleUpdateTrackingEntry = async () => {
        try {
            const formData = new FormData();
            
            // Add basic fields
            formData.append('activity_id', editingEntry.activity_id);
            formData.append('activity_date', editingEntry.activity_date);
            formData.append('activity_time', editingEntry.activity_time);
            formData.append('activity_location', editingEntry.activity_location);
            
            // Add optional remark if provided
            if (editingEntry.activity_remark) {
                formData.append('activity_remark', editingEntry.activity_remark);
            }

            // Add optional vehicle (registered vehicle id and/or a typed number).
            // Always send vehicle_number so clearing it persists.
            if (editingEntry.vehicle_id) {
                formData.append('vehicle_id', editingEntry.vehicle_id);
            }
            formData.append('vehicle_number', editingEntry.vehicle_number || '');

            // Only add delivery confirmation fields for activity ID 8
            if (editingEntry.activity_id == 8) {
                formData.append('is_sealed_signed', editingEntry.is_sealed_signed ? '1' : '0');
                
                // Only add image if one is selected
                if (editingEntry.signed_copy_image) {
                    formData.append('signed_copy_image', editingEntry.signed_copy_image);
                }
            }

            const response = await trackingService.updateTrackingEntry(editingEntry.id, formData);
            if (response.success) {
                showSuccess('Tracking entry updated successfully');
                setEditTrackingDialogOpen(false);
                setEditingEntry(null);
                setImagePreview(null);
                
                // Refresh tracking history
                if (selectedShipment) {
                    handleSelectShipment(selectedShipment);
                }
            } else {
                if (response.errors) {
                    const errorMessages = Object.values(response.errors).flat();
                    showError(errorMessages.join(', '));
                } else {
                    showError(response.message || 'Failed to update tracking entry');
                }
            }
        } catch (error) {
            console.error('Error updating tracking entry:', error);
            showError('Failed to update tracking entry');
        }
    };

    // Delete tracking entry
    const handleDeleteEntry = (entry) => {
        setDeleteEntry(entry);
        setDeleteConfirmDialogOpen(true);
        handleMenuClose();
    };

    const confirmDeleteEntry = async () => {
        try {
            const response = await trackingService.deleteTrackingEntry(deleteEntry.id);
            if (response.success) {
                showSuccess('Tracking entry deleted successfully');
                setDeleteConfirmDialogOpen(false);
                setDeleteEntry(null);
                
                // Refresh tracking history
                if (selectedShipment) {
                    handleSelectShipment(selectedShipment);
                }
            } else {
                if (response.errors) {
                    const errorMessages = Object.values(response.errors).flat();
                    showError(errorMessages.join(', '));
                } else {
                    showError(response.message || 'Failed to delete tracking entry');
                }
            }
        } catch (error) {
            console.error('Error deleting tracking entry:', error);
            showError('Failed to delete tracking entry');
        }
    };

    // Handle image viewing
    const handleImageView = (imageUrl, altText = 'Signed delivery copy') => {
        setSelectedImage({ url: imageUrl, alt: altText });
        setImageViewerOpen(true);
    };

    const handleCloseImageViewer = () => {
        setImageViewerOpen(false);
        setSelectedImage(null);
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'delivered':
                return 'success';
            case 'out for delivery':
                return 'warning';
            case 'in transit':
                return 'info';
            case 'picked up':
                return 'primary';
            default:
                return 'default';
        }
    };

    const getTimelineDotColor = (activity) => {
        const activityLower = activity.toLowerCase();
        if (activityLower.includes('delivered')) return 'success';
        if (activityLower.includes('out for delivery')) return 'warning';
        if (activityLower.includes('picked') || activityLower.includes('collected')) return 'primary';
        if (activityLower.includes('arrived') || activityLower.includes('reached')) return 'info';
        return 'grey';
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'delivered':
                return <DeliveredIcon />;
            case 'out for delivery':
                return <TransitIcon />;
            case 'in transit':
                return <PackageIcon />;
            case 'picked up':
                return <CheckIcon />;
            default:
                return <PendingIcon />;
        }
    };

    // Check if an entry is a booking entry (should not be editable/deletable)
    const isBookingEntry = (activityName, activityId = null) => {
        // If we have activity_id, use it for precise checking (activity_id = 1 is booking)
        if (activityId !== null) {
            return activityId === 1;
        }
        
        // Fallback to name-based checking for backward compatibility
        const bookingKeywords = ['booked', 'booking', 'shipment booked', 'order placed'];
        return bookingKeywords.some(keyword => 
            activityName.toLowerCase().includes(keyword.toLowerCase())
        );
    };

    return (
        <DashboardLayout>
            {/* Enhanced Header */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar 
                        sx={{ 
                            bgcolor: 'primary.main', 
                            mr: 2, 
                            width: 48, 
                            height: 48 
                        }}
                    >
                        <TimelineIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h4" component="h1" fontWeight="bold">
                            Tracking Management
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Monitor and update shipment tracking information in real-time
                        </Typography>
                    </Box>
                </Box>
            </Box>
            
            <Grid container spacing={3}>
                {/* Enhanced Search Section */}
                <Grid item xs={12} lg={4}>
                    <Paper 
                        elevation={2} 
                        sx={{ 
                            p: 3, 
                            height: 'fit-content',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            mb: 3
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <SearchIcon sx={{ mr: 1, fontSize: 28 }} />
                            <Typography variant="h6" fontWeight="bold">
                                Tracking Management
                            </Typography>
                        </Box>

                        <TextField
                            fullWidth
                            placeholder="Search all active bookings by AWB, Name, or Mobile"
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'rgba(0,0,0,0.6)' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (searchLoading) && (
                                    <InputAdornment position="end">
                                        <CircularProgress size={20} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    bgcolor: 'white',
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'transparent',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                }
                            }}
                            sx={{ mb: 3 }}
                        />

                        {/* Search Results */}
                        <Box sx={{ 
                            maxHeight: 400, 
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': {
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: 'rgba(255,255,255,0.1)',
                                borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'rgba(255,255,255,0.3)',
                                borderRadius: '3px',
                            },
                        }}>
                            <Stack spacing={2}>
                                {searchResults.map((booking) => (
                                    <Fade key={booking.awb_no} in={true}>
                                        <Card 
                                            variant="outlined"
                                            sx={{ 
                                                cursor: 'pointer',
                                                bgcolor: selectedShipment?.awb_no === booking.awb_no 
                                                    ? 'rgba(255,255,255,0.95)' 
                                                    : 'rgba(255,255,255,0.9)',
                                                transition: 'all 0.3s ease',
                                                border: selectedShipment?.awb_no === booking.awb_no 
                                                    ? '2px solid #1976d2' 
                                                    : '1px solid rgba(255,255,255,0.3)',
                                                '&:hover': { 
                                                    bgcolor: 'rgba(255,255,255,0.95)',
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                                                },
                                            }}
                                            onClick={() => handleSelectShipment(booking)}
                                        >
                                            <CardContent sx={{ py: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <TrackIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                                                    <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                                                        {booking.awb_no}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    <LocationIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                                    {booking.route}
                                                </Typography>
                                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                                                    {booking.shipper} → {booking.recipient}
                                                </Typography>
                                                <Chip 
                                                    label={booking.status} 
                                                    size="small" 
                                                    color={getStatusColor(booking.status)}
                                                    icon={getStatusIcon(booking.status)}
                                                    sx={{ 
                                                        fontWeight: 'bold',
                                                        '& .MuiChip-icon': {
                                                            fontSize: 16
                                                        }
                                                    }}
                                                />
                                            </CardContent>
                                        </Card>
                                    </Fade>
                                ))}
                            </Stack>
                        </Box>
                    </Paper>

                    {/* Enhanced Action Buttons */}
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <HistoryIcon sx={{ mr: 1 }} />
                            Quick Actions
                        </Typography>
                        
                        <Stack spacing={2}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    // Pre-fill AWB if available from navigation or selected shipment
                                    let awbNumber = '';
                                    if (location.state?.awbNumber) {
                                        awbNumber = location.state.awbNumber;
                                    } else if (selectedShipment?.awb_no) {
                                        awbNumber = selectedShipment.awb_no;
                                    }
                                    
                                    setNewTrackingData({
                                        awb_no: awbNumber,
                                        activity_id: '',
                                        activity_date: new Date().toISOString().split('T')[0],
                                        activity_time: new Date().toTimeString().slice(0, 5),
                                        activity_location: '',
                                        activity_remark: '',
                                        vehicle_id: null,
                                        vehicle_number: '',
                                        is_sealed_signed: false,
                                        signed_copy_image: null
                                    });
                                    setImagePreview(null);
                                    setAddTrackingDialogOpen(true);
                                }}
                                fullWidth
                                sx={{
                                    py: 1.5,
                                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                                    '&:hover': {
                                        background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
                                    }
                                }}
                            >
                                Add Tracking Entry
                            </Button>
                            


                            {selectedShipment && (
                                <Button
                                    variant="text"
                                    size="large"
                                    startIcon={<RefreshIcon />}
                                    onClick={() => handleSelectShipment(selectedShipment)}
                                    fullWidth
                                    sx={{ py: 1.5 }}
                                >
                                    Refresh Details
                                </Button>
                            )}
                        </Stack>
                    </Paper>
                </Grid>

                {/* Enhanced Tracking Details Section */}
                <Grid item xs={12} lg={8}>
                    {selectedShipment ? (
                        <Fade in={true}>
                            <Paper elevation={2} sx={{ p: 3 }}>
                                {loading ? (
                                    <Box sx={{ textAlign: 'center', py: 6 }}>
                                        <CircularProgress size={60} thickness={4} />
                                        <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
                                            Loading tracking details...
                                        </Typography>
                                        <LinearProgress sx={{ mt: 2, width: '60%', mx: 'auto' }} />
                                    </Box>
                                ) : trackingHistory ? (
                                    <>
                                        {/* Enhanced Shipment Info Card */}
                                        <Card 
                                            elevation={0} 
                                            sx={{ 
                                                mb: 4, 
                                                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                                                border: '1px solid #e0e0e0'
                                            }}
                                        >
                                            <CardContent sx={{ p: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}>
                                                        <PackageIcon sx={{ fontSize: 28 }} />
                                                    </Avatar>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                                                            {trackingHistory.booking_info.awb_no}
                                                        </Typography>
                                                        <Chip 
                                                            label={trackingHistory.booking_info.status} 
                                                            color={getStatusColor(trackingHistory.booking_info.status)}
                                                            icon={getStatusIcon(trackingHistory.booking_info.status)}
                                                            sx={{ 
                                                                fontWeight: 'bold',
                                                                fontSize: '0.875rem',
                                                                height: 32
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                                
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12} md={6}>
                                                        <Paper elevation={1} sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.8)' }}>
                                                            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <HomeIcon sx={{ mr: 1, fontSize: 18 }} />
                                                                Sender Information
                                                            </Typography>
                                                            <Typography variant="h6" fontWeight="bold">
                                                                {trackingHistory.booking_info.from_city}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                <BusinessIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                                                {trackingHistory.booking_info.shipper.name}
                                                                {(trackingHistory.booking_info.shipper.company && trackingHistory.booking_info.shipper.name != trackingHistory.booking_info.shipper.company) &&
                                                                    ` - ${trackingHistory.booking_info.shipper.company}`}
                                                            </Typography>
                                                        </Paper>
                                                    </Grid>
                                                    
                                                    <Grid item xs={12} md={6}>
                                                        <Paper elevation={1} sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.8)' }}>
                                                            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <LocationIcon sx={{ mr: 1, fontSize: 18 }} />
                                                                Recipient Information
                                                            </Typography>
                                                            <Typography variant="h6" fontWeight="bold">
                                                                {trackingHistory.booking_info.to_city}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                <BusinessIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                                                {trackingHistory.booking_info.recipient.name}
                                                                {(trackingHistory.booking_info.recipient.company && trackingHistory.booking_info.recipient.name != trackingHistory.booking_info.recipient.company) &&
                                                                    ` - ${trackingHistory.booking_info.recipient.company}`}
                                                            </Typography>
                                                        </Paper>
                                                    </Grid>

                                                    <Grid item xs={6} sm={3}>
                                                        <Box sx={{ textAlign: 'center', p: 2 }}>
                                                            <Typography variant="h4" fontWeight="bold" color="primary.main">
                                                                {trackingHistory.booking_info.weight}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                KG Weight
                                                            </Typography>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={6} sm={3}>
                                                        <Box sx={{ textAlign: 'center', p: 2 }}>
                                                            <Typography variant="h4" fontWeight="bold" color="primary.main">
                                                                {trackingHistory.booking_info.packages}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Package(s)
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>

                                        {/* Enhanced Tracking Timeline */}
                                        <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                                            <CardContent sx={{ p: 0 }}>
                                                <Box sx={{ p: 3, bgcolor: 'grey.50', borderBottom: '1px solid #e0e0e0' }}>
                                                    <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <TimelineIcon sx={{ mr: 2, color: 'primary.main' }} />
                                                        Tracking Timeline
                                                        <Badge 
                                                            badgeContent={trackingHistory.tracking_history.length} 
                                                            color="primary" 
                                                            sx={{ ml: 2 }}
                                                        />
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Complete shipment journey tracking
                                                    </Typography>
                                                </Box>

                                                <Box sx={{ p: 3 }}>
                                                    {trackingHistory.tracking_history.length === 0 ? (
                                                        <Alert 
                                                            severity="info" 
                                                            icon={<TimelineIcon />}
                                                            sx={{ 
                                                                bgcolor: 'info.50',
                                                                border: '1px solid',
                                                                borderColor: 'info.200'
                                                            }}
                                                        >
                                                            <Typography variant="h6" gutterBottom>
                                                                No tracking updates available yet
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                Tracking information will appear here once the shipment starts moving.
                                                            </Typography>
                                                        </Alert>
                                                    ) : (
                                                        <Stack spacing={3}>
                                                            {trackingHistory.tracking_history.map((entry, index) => (
                                                                <Slide 
                                                                    key={entry.id} 
                                                                    direction="up" 
                                                                    in={true} 
                                                                    timeout={300 + (index * 100)}
                                                                >
                                                                    <Box sx={{ display: 'flex', position: 'relative' }}>
                                                                        {/* Timeline connector */}
                                                                        {index < trackingHistory.tracking_history.length - 1 && (
                                                                            <Box
                                                                                sx={{
                                                                                    position: 'absolute',
                                                                                    left: 28,
                                                                                    top: 56,
                                                                                    width: 2,
                                                                                    height: 40,
                                                                                    bgcolor: 'grey.300',
                                                                                    zIndex: 0
                                                                                }}
                                                                            />
                                                                        )}

                                                                        {/* Timeline dot */}
                                                                        <Avatar 
                                                                            sx={{ 
                                                                                bgcolor: `${getTimelineDotColor(entry.activity)}.main`,
                                                                                width: 56,
                                                                                height: 56,
                                                                                mr: 3,
                                                                                border: index === 0 ? '4px solid' : '2px solid white',
                                                                                borderColor: index === 0 ? `${getTimelineDotColor(entry.activity)}.dark` : 'white',
                                                                                boxShadow: index === 0 ? '0 8px 25px rgba(0,0,0,0.15)' : '0 4px 12px rgba(0,0,0,0.1)',
                                                                                zIndex: 1
                                                                            }}
                                                                        >
                                                                            {getTimelineDotColor(entry.activity) === 'success' ? (
                                                                                <CheckIcon sx={{ fontSize: 28 }} />
                                                                            ) : (
                                                                                <CircleIcon sx={{ fontSize: 16 }} />
                                                                            )}
                                                                        </Avatar>

                                                                        {/* Content */}
                                                                        <Card 
                                                                            elevation={index === 0 ? 3 : 1}
                                                                            sx={{ 
                                                                                flex: 1,
                                                                                bgcolor: index === 0 ? 'primary.50' : 'background.paper',
                                                                                border: index === 0 ? '2px solid' : '1px solid #e0e0e0',
                                                                                borderColor: index === 0 ? 'primary.main' : '#e0e0e0'
                                                                            }}
                                                                        >
                                                                            <CardContent sx={{ p: 3 }}>
                                                                                {/* Header with activity and time */}
                                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                                                    <Typography variant="h6" fontWeight="bold" color={index === 0 ? 'primary.main' : 'text.primary'}>
                                                                                        {entry.activity}
                                                                                        {index === 0 && (
                                                                                            <Chip 
                                                                                                label="Latest" 
                                                                                                size="small" 
                                                                                                color="primary" 
                                                                                                sx={{ ml: 2, fontSize: '0.75rem' }}
                                                                                            />
                                                                                        )}
                                                                                        {isBookingEntry(entry.activity, entry.activity_id) && (
                                                                                            <Chip 
                                                                                                label="Protected" 
                                                                                                size="small" 
                                                                                                color="warning" 
                                                                                                sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                                                                                            />
                                                                                        )}
                                                                                    </Typography>
                                                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                                                    <Box sx={{ textAlign: 'right', minWidth: 120 }}>
                                                                                        <Typography variant="body2" fontWeight="bold">
                                                                                            {entry.date}
                                                                                        </Typography>
                                                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                                                                            <ClockIcon sx={{ fontSize: 12, mr: 0.5 }} />
                                                                                            {entry.time}
                                                                                        </Typography>
                                                                                        </Box>

                                                                                                                                                {/* Edit/Delete Menu - Only for authorized users and non-booking entries */}
                                                        {canEditTracking && !isBookingEntry(entry.activity, entry.activity_id) && (
                                                            <Tooltip title="Edit or Delete Entry">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => handleMenuOpen(e, entry.id)}
                                                                    sx={{ 
                                                                        bgcolor: 'action.hover',
                                                                        '&:hover': { bgcolor: 'action.selected' }
                                                                    }}
                                                                >
                                                                    <MoreIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        
                                                        {/* Show protected indicator for booking entries */}
                                                        {isBookingEntry(entry.activity, entry.activity_id) && (
                                                            <Tooltip title="Booking entry cannot be modified">
                                                                <IconButton
                                                                    size="small"
                                                                    disabled
                                                                    sx={{ 
                                                                        color: 'warning.main',
                                                                        cursor: 'not-allowed'
                                                                    }}
                                                                >
                                                                    <WarningIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                                                    </Box>
                                                                                </Box>

                                                                                {/* Location */}
                                                                                <Typography 
                                                                                    variant="body2" 
                                                                                    color="text.secondary" 
                                                                                    sx={{ 
                                                                                        display: 'flex', 
                                                                                        alignItems: 'center', 
                                                                                        mb: entry.remark ? 2 : 1,
                                                                                        fontWeight: 500
                                                                                    }}
                                                                                >
                                                                                    <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
                                                                                    {entry.location}
                                                                                </Typography>

                                                                                {/* Vehicle */}
                                                                                {entry.vehicle_number && (
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        color="text.secondary"
                                                                                        sx={{
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            mb: entry.remark ? 2 : 1,
                                                                                            fontWeight: 500
                                                                                        }}
                                                                                    >
                                                                                        <VehicleIcon sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
                                                                                        {entry.vehicle_number}
                                                                                    </Typography>
                                                                                )}

                                                                                {/* Remark */}
                                                                                {entry.remark && (
                                                                                    <Paper 
                                                                                        elevation={0} 
                                                                                        sx={{ 
                                                                                            p: 2, 
                                                                                            bgcolor: 'grey.100', 
                                                                                            borderRadius: 2,
                                                                                            mb: 2
                                                                                        }}
                                                                                    >
                                                                                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                                                                            "{entry.remark}"
                                                                                        </Typography>
                                                                                    </Paper>
                                                                                )}

                                                                                {/* Delivery Confirmation Info - Show for delivered activities */}
                                                                                {entry.is_sealed_signed !== undefined && (
                                                                                    <Paper 
                                                                                        elevation={0} 
                                                                                        sx={{ 
                                                                                            p: 2, 
                                                                                            bgcolor: entry.is_sealed_signed ? 'success.50' : 'warning.50', 
                                                                                            border: '1px solid',
                                                                                            borderColor: entry.is_sealed_signed ? 'success.200' : 'warning.200',
                                                                                            borderRadius: 2,
                                                                                            mb: 2
                                                                                        }}
                                                                                    >
                                                                                        <Typography 
                                                                                            variant="subtitle2" 
                                                                                            sx={{ 
                                                                                                display: 'flex', 
                                                                                                alignItems: 'center',
                                                                                                color: entry.is_sealed_signed ? 'success.main' : 'warning.main',
                                                                                                mb: 1
                                                                                            }}
                                                                                        >
                                                                                            <SealIcon sx={{ mr: 1, fontSize: 16 }} />
                                                                                            Delivery Confirmation
                                                                                        </Typography>
                                                                                        <Typography variant="body2" color="text.secondary">
                                                                                            {entry.is_sealed_signed 
                                                                                                ? "✓ Package delivered with seal & signature verification" 
                                                                                                : "Package delivered without seal verification"}
                                                                                        </Typography>

                                                                                        {/* Signed Copy Image */}
                                                                                        {entry.signed_copy_url && (
                                                                                            <Box sx={{ mt: 2 }}>
                                                                                                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                                                                                                    Signed Copy:
                                                                                                </Typography>
                                                                                                <Paper 
                                                                                                    elevation={1} 
                                                                                                    sx={{ 
                                                                                                        p: 1, 
                                                                                                        display: 'inline-block',
                                                                                                        cursor: 'pointer',
                                                                                                        position: 'relative',
                                                                                                        '&:hover': {
                                                                                                            elevation: 3,
                                                                                                            '& .zoom-overlay': {
                                                                                                                opacity: 1
                                                                                                            }
                                                                                                        }
                                                                                                    }}
                                                                                                    onClick={() => handleImageView(entry.signed_copy_url, 'Signed delivery copy')}
                                                                                                >
                                                                                                    <img
                                                                                                        src={entry.signed_copy_url}
                                                                                                        alt="Signed delivery copy"
                                                                                                        style={{
                                                                                                            maxWidth: '200px',
                                                                                                            maxHeight: '150px',
                                                                                                            width: 'auto',
                                                                                                            height: 'auto',
                                                                                                            borderRadius: '4px'
                                                                                                        }}
                                                                                                    />
                                                                                                    <Box
                                                                                                        className="zoom-overlay"
                                                                                                        sx={{
                                                                                                            position: 'absolute',
                                                                                                            top: 0,
                                                                                                            left: 0,
                                                                                                            right: 0,
                                                                                                            bottom: 0,
                                                                                                            bgcolor: 'rgba(0,0,0,0.5)',
                                                                                                            display: 'flex',
                                                                                                            alignItems: 'center',
                                                                                                            justifyContent: 'center',
                                                                                                            opacity: 0,
                                                                                                            transition: 'opacity 0.2s ease',
                                                                                                            borderRadius: '4px'
                                                                                                        }}
                                                                                                    >
                                                                                                        <ZoomInIcon sx={{ color: 'white', fontSize: 32 }} />
                                                                                                    </Box>
                                                                                                </Paper>
                                                                                                <Typography variant="caption" color="primary.main" display="block" sx={{ mt: 0.5 }}>
                                                                                                    Click to view full size
                                                                                                </Typography>
                                                                                            </Box>
                                                                                        )}
                                                                                    </Paper>
                                                                                )}
                                                                                
                                                                                {/* Employee info */}
                                                                                <Typography 
                                                                                    variant="caption" 
                                                                                    color="text.secondary" 
                                                                                    sx={{ display: 'flex', alignItems: 'center' }}
                                                                                >
                                                                                    <PersonIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                                                                    Updated by: {entry.employee}
                                                                                </Typography>
                                                                            </CardContent>
                                                                        </Card>
                                                                    </Box>
                                                                </Slide>
                                                            ))}
                                                        </Stack>
                                                    )}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </>
                                ) : (
                                    <Alert severity="error" sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography variant="h6" gutterBottom>
                                            Failed to load tracking details
                                        </Typography>
                                        <Typography variant="body2">
                                            Please try again or contact support if the problem persists.
                                        </Typography>
                                        <Button 
                                            variant="contained" 
                                            onClick={() => handleSelectShipment(selectedShipment)}
                                            sx={{ mt: 2 }}
                                            startIcon={<RefreshIcon />}
                                        >
                                            Retry
                                        </Button>
                                    </Alert>
                                )}
                            </Paper>
                        </Fade>
                    ) : (
                        <Paper 
                            elevation={2} 
                            sx={{ 
                                p: 6, 
                                textAlign: 'center',
                                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                                border: '2px dashed #ccc'
                            }}
                        >
                            <Avatar sx={{ 
                                bgcolor: 'primary.main', 
                                width: 80, 
                                height: 80, 
                                mx: 'auto', 
                                mb: 3 
                            }}>
                                <TrackIcon sx={{ fontSize: 40 }} />
                            </Avatar>
                            <Typography variant="h5" fontWeight="bold" color="text.primary" gutterBottom>
                                Select a Shipment to View Details
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                Search for shipments using AWB number, customer name, or mobile number
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Chip 
                                    icon={<SearchIcon />} 
                                    label="Search by AWB" 
                                    variant="outlined" 
                                    color="primary"
                                />
                                <Chip 
                                    icon={<PersonIcon />} 
                                    label="Search by Name" 
                                    variant="outlined" 
                                    color="primary"
                                />
                                <Chip 
                                    icon={<PhoneIcon />} 
                                    label="Search by Mobile" 
                                    variant="outlined" 
                                    color="primary"
                                />
                            </Box>
                        </Paper>
                    )}
                </Grid>
            </Grid>

            {/* Enhanced Add Tracking Entry Dialog */}
            <Dialog 
                open={addTrackingDialogOpen} 
                onClose={() => setAddTrackingDialogOpen(false)}
                maxWidth="md"
                fullWidth
                scroll="body"
                BackdropProps={{
                    sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
                }}
                PaperProps={{
                    elevation: 8,
                    sx: { 
                        borderRadius: 3,
                        maxHeight: '90vh',
                        my: 4,
                        mx: 2,
                        position: 'relative',
                        top: 0
                    }
                }}
            >
                <DialogTitle sx={{ py: 2, bgcolor: 'primary.main', color: 'white' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AddIcon sx={{ mr: 2 }} />
                        <Box>
                            <Typography variant="h6" fontWeight="bold">
                                Add New Tracking Entry
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Record a new activity for the shipment
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 6, pb: 2, px: 3 }}>
                    {/* Extra spacing to ensure AWB field is fully visible */}
                    <Box sx={{ height: 16 }} />
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                label="AWB Number"
                                placeholder={selectedShipment ? `Auto-filled: ${selectedShipment.awb_no}` : "Enter AWB number"}
                                value={newTrackingData.awb_no}
                                onChange={(e) => setNewTrackingData({...newTrackingData, awb_no: e.target.value})}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <TrackIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                helperText={selectedShipment && newTrackingData.awb_no === selectedShipment.awb_no ? 
                                    `✓ Auto-populated from selected shipment` : 
                                    "Enter the AWB number for tracking entry"
                                }
                            />
                        </Grid>
                        
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Activity</InputLabel>
                                <Select
                                    value={newTrackingData.activity_id}
                                    onChange={(e) => handleActivityChange(e.target.value)}
                                    label="Activity"
                                >
                                    {activities.map(activity => (
                                        <MenuItem key={activity.id} value={activity.id}>
                                            {activity.name}
                                            {activity.id === 8 && (
                                                <Chip 
                                                    label="Delivery Confirmation" 
                                                    size="small" 
                                                    color="success" 
                                                    sx={{ ml: 1 }}
                                                />
                                            )}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        
                        <Grid item xs={6}>
                            <TextField
                                label="Date"
                                type="date"
                                value={newTrackingData.activity_date}
                                onChange={(e) => setNewTrackingData({...newTrackingData, activity_date: e.target.value})}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <TodayIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Time"
                                type="time"
                                value={newTrackingData.activity_time}
                                onChange={(e) => setNewTrackingData({...newTrackingData, activity_time: e.target.value})}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <TimeIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        
                        <Grid item xs={12}>
                            <TextField
                                label="Location"
                                value={newTrackingData.activity_location}
                                onChange={(e) => setNewTrackingData({...newTrackingData, activity_location: e.target.value})}
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LocationIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        
                        <Grid item xs={12}>
                            <TextField
                                label="Remarks (Optional)"
                                value={newTrackingData.activity_remark}
                                onChange={(e) => setNewTrackingData({...newTrackingData, activity_remark: e.target.value})}
                                multiline
                                rows={3}
                                fullWidth
                                placeholder="Add any additional notes about this tracking update..."
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Autocomplete
                                freeSolo
                                options={vehicles}
                                getOptionLabel={vehicleOptionLabel}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                value={resolveVehicleValue(newTrackingData)}
                                onChange={(e, newValue) => applyVehicleSelection(setNewTrackingData, newValue)}
                                onInputChange={(e, newInput, reason) => {
                                    // Track typed (temporary) vehicle numbers as free text
                                    if (reason === 'input') {
                                        setNewTrackingData(prev => ({ ...prev, vehicle_id: null, vehicle_number: newInput }));
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Vehicle (Optional)"
                                        placeholder="Select a registered vehicle or type a number"
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <VehicleIcon />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Delivery Confirmation Fields - Show only for "Delivered Successfully" (ID: 8) */}
                        {newTrackingData.activity_id === 8 && (
                            <>
                                <Grid item xs={12}>
                                    <Paper 
                                        elevation={1} 
                                        sx={{ 
                                            p: 2, 
                                            bgcolor: 'success.50', 
                                            border: '1px solid',
                                            borderColor: 'success.200'
                                        }}
                                    >
                                        <Typography variant="h6" color="success.main" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                            <SealIcon sx={{ mr: 1 }} />
                                            Delivery Confirmation
                                        </Typography>
                                        
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={newTrackingData.is_sealed_signed}
                                                    onChange={(e) => setNewTrackingData({
                                                        ...newTrackingData, 
                                                        is_sealed_signed: e.target.checked
                                                    })}
                                                    color="success"
                                                />
                                            }
                                            label={
                                                <Typography variant="body1" fontWeight="medium">
                                                    Package delivered with seal & signature verification
                                                </Typography>
                                            }
                                        />
                                        
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                            Check this box to confirm the package was delivered intact with proper recipient verification
                                        </Typography>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12}>
                                    <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                            <ImageIcon sx={{ mr: 1 }} />
                                            Signed Copy Upload (Optional)
                                        </Typography>
                                        
                                        <Box sx={{ mt: 2 }}>
                                            <Input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageUpload}
                                                sx={{ display: 'none' }}
                                                id="signed-copy-upload"
                                            />
                                            <label htmlFor="signed-copy-upload">
                                                <Button
                                                    variant="outlined"
                                                    component="span"
                                                    startIcon={<UploadIcon />}
                                                    sx={{ mr: 2 }}
                                                >
                                                    Upload Signed Copy
                                                </Button>
                                            </label>
                                            
                                            {imagePreview && (
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={removeImage}
                                                >
                                                    Remove Image
                                                </Button>
                                            )}
                                        </Box>

                                        {imagePreview && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    Preview:
                                                </Typography>
                                                <Paper 
                                                    elevation={2} 
                                                    sx={{ 
                                                        p: 1, 
                                                        display: 'inline-block',
                                                        maxWidth: '100%'
                                                    }}
                                                >
                                                    <img
                                                        src={imagePreview}
                                                        alt="Signed copy preview"
                                                        style={{
                                                            maxWidth: '300px',
                                                            maxHeight: '200px',
                                                            width: 'auto',
                                                            height: 'auto',
                                                            borderRadius: '4px'
                                                        }}
                                                    />
                                                </Paper>
                                            </Box>
                                        )}
                                        
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                            Upload a photo of the signed delivery receipt or confirmation document (Max: 5MB)
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
                    <Button 
                        onClick={() => {
                            setAddTrackingDialogOpen(false);
                            // Reset form but keep AWB if it was auto-populated from selected shipment
                            const keepAwb = selectedShipment?.awb_no || location.state?.awbNumber || '';
                            setNewTrackingData({
                                awb_no: keepAwb,
                                activity_id: '',
                                activity_date: new Date().toISOString().split('T')[0],
                                activity_time: new Date().toTimeString().slice(0, 5),
                                activity_location: '',
                                activity_remark: '',
                                vehicle_id: null,
                                vehicle_number: '',
                                is_sealed_signed: false,
                                signed_copy_image: null
                            });
                            setImagePreview(null);
                        }}
                        variant="outlined"
                        size="large"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleAddTrackingEntry}
                        variant="contained"
                        size="large"
                        disabled={!newTrackingData.awb_no || !newTrackingData.activity_id || !newTrackingData.activity_location}
                        startIcon={<AddIcon />}
                    >
                        Add Entry
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Action Menu for Edit/Delete */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                PaperProps={{
                    elevation: 3,
                    sx: {
                        minWidth: 160,
                        '& .MuiMenuItem-root': {
                            px: 2,
                            py: 1
                        }
                    }
                }}
            >
                <MenuItem 
                    onClick={() => {
                        const entry = trackingHistory?.tracking_history.find(e => e.id === selectedEntryId);
                        if (entry) handleEditEntry(entry);
                    }}
                    sx={{ color: 'primary.main' }}
                >
                    <ListItemIcon>
                        <EditIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Edit Entry" />
                </MenuItem>
                <Divider />
                <MenuItem 
                    onClick={() => {
                        const entry = trackingHistory?.tracking_history.find(e => e.id === selectedEntryId);
                        if (entry) handleDeleteEntry(entry);
                    }}
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText primary="Delete Entry" />
                </MenuItem>
            </Menu>

            {/* Edit Tracking Entry Dialog */}
            <Dialog 
                open={editTrackingDialogOpen} 
                onClose={() => {
                    setEditTrackingDialogOpen(false);
                    setEditingEntry(null);
                    setImagePreview(null);
                }}
                maxWidth="md"
                fullWidth
                scroll="body"
                BackdropProps={{
                    sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
                }}
                PaperProps={{
                    elevation: 8,
                    sx: { 
                        borderRadius: 3,
                        maxHeight: '90vh',
                        my: 4,
                        mx: 2,
                        position: 'relative',
                        top: 0
                    }
                }}
            >
                <DialogTitle sx={{ py: 2, bgcolor: 'warning.main', color: 'white' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EditIcon sx={{ mr: 2 }} />
                        <Box>
                            <Typography variant="h6" fontWeight="bold">
                                Edit Tracking Entry
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Modify the tracking information for this entry
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>
                {editingEntry && (
                    <DialogContent sx={{ pt: 6, pb: 2, px: 3 }}>
                        {/* Extra spacing to ensure fields are fully visible */}
                        <Box sx={{ height: 16 }} />
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Activity</InputLabel>
                            <Select
                                        value={editingEntry.activity_id}
                                        onChange={(e) => handleEditActivityChange(e.target.value)}
                                label="Activity"
                            >
                                {activities.map(activity => (
                                    <MenuItem key={activity.id} value={activity.id}>
                                        {activity.name}
                                                {activity.id === 8 && (
                                                    <Chip 
                                                        label="Delivery Confirmation" 
                                                        size="small" 
                                                        color="success" 
                                                        sx={{ ml: 1 }}
                                                    />
                                                )}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                            </Grid>
                            
                            <Grid item xs={6}>
                                <TextField
                                    label="Date"
                                    type="date"
                                    value={editingEntry.activity_date}
                                    onChange={(e) => setEditingEntry({...editingEntry, activity_date: e.target.value})}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <TodayIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Time"
                                    type="time"
                                    value={editingEntry.activity_time}
                                    onChange={(e) => setEditingEntry({...editingEntry, activity_time: e.target.value})}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <TimeIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                        <TextField
                            label="Location"
                                    value={editingEntry.activity_location}
                                    onChange={(e) => setEditingEntry({...editingEntry, activity_location: e.target.value})}
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LocationIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                            </Grid>
                        
                            <Grid item xs={12}>
                        <TextField
                            label="Remarks (Optional)"
                                    value={editingEntry.activity_remark}
                                    onChange={(e) => setEditingEntry({...editingEntry, activity_remark: e.target.value})}
                            multiline
                                    rows={3}
                            fullWidth
                                    placeholder="Add any additional notes about this tracking update..."
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Autocomplete
                                    freeSolo
                                    options={vehicles}
                                    getOptionLabel={vehicleOptionLabel}
                                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                    value={resolveVehicleValue(editingEntry)}
                                    onChange={(e, newValue) => applyVehicleSelection(setEditingEntry, newValue)}
                                    onInputChange={(e, newInput, reason) => {
                                        if (reason === 'input') {
                                            setEditingEntry(prev => ({ ...prev, vehicle_id: null, vehicle_number: newInput }));
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Vehicle (Optional)"
                                            placeholder="Select a registered vehicle or type a number"
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        <InputAdornment position="start">
                                                            <VehicleIcon />
                                                        </InputAdornment>
                                                        {params.InputProps.startAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Delivery Confirmation Fields - Show only for "Delivered Successfully" (ID: 8) */}
                            {editingEntry.activity_id === 8 && (
                                <>
                                    <Grid item xs={12}>
                                        <Paper 
                                            elevation={1} 
                                            sx={{ 
                                                p: 2, 
                                                bgcolor: 'success.50', 
                                                border: '1px solid',
                                                borderColor: 'success.200'
                                            }}
                                        >
                                            <Typography variant="h6" color="success.main" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                                <SealIcon sx={{ mr: 1 }} />
                                                Delivery Confirmation
                                            </Typography>
                                            
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={editingEntry.is_sealed_signed}
                                                        onChange={(e) => setEditingEntry({
                                                            ...editingEntry, 
                                                            is_sealed_signed: e.target.checked
                                                        })}
                                                        color="success"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body1" fontWeight="medium">
                                                        Package delivered with seal & signature verification
                                                    </Typography>
                                                }
                                            />
                                            
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                                Check this box to confirm the package was delivered intact with proper recipient verification
                                            </Typography>
                                        </Paper>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50' }}>
                                            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                                <ImageIcon sx={{ mr: 1 }} />
                                                Signed Copy Upload (Optional)
                                            </Typography>
                                            
                                            <Box sx={{ mt: 2 }}>
                                                <Input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleEditImageUpload}
                                                    sx={{ display: 'none' }}
                                                    id="edit-signed-copy-upload"
                                                />
                                                <label htmlFor="edit-signed-copy-upload">
                                                    <Button
                                                        variant="outlined"
                                                        component="span"
                                                        startIcon={<UploadIcon />}
                                                        sx={{ mr: 2 }}
                                                    >
                                                        Upload Signed Copy
                                                    </Button>
                                                </label>
                                                
                                                {imagePreview && (
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={removeEditImage}
                                                    >
                                                        Remove Image
                                                    </Button>
                                                )}
                                            </Box>

                                            {imagePreview && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Preview:
                                                    </Typography>
                                                    <Paper 
                                                        elevation={2} 
                                                        sx={{ 
                                                            p: 1, 
                                                            display: 'inline-block',
                                                            maxWidth: '100%'
                                                        }}
                                                    >
                                                        <img
                                                            src={imagePreview}
                                                            alt="Signed copy preview"
                                                            style={{
                                                                maxWidth: '300px',
                                                                maxHeight: '200px',
                                                                width: 'auto',
                                                                height: 'auto',
                                                                borderRadius: '4px'
                                                            }}
                                                        />
                                                    </Paper>
                                                </Box>
                                            )}
                                            
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                                Upload a photo of the signed delivery receipt or confirmation document (Max: 5MB)
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                </DialogContent>
                )}
                <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
                    <Button 
                        onClick={() => {
                            setEditTrackingDialogOpen(false);
                            setEditingEntry(null);
                            setImagePreview(null);
                        }}
                        variant="outlined"
                        size="large"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleUpdateTrackingEntry}
                        variant="contained"
                        size="large"
                        disabled={!editingEntry?.activity_id || !editingEntry?.activity_location}
                        startIcon={<SaveIcon />}
                        color="warning"
                    >
                        Update Entry
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmDialogOpen}
                onClose={() => setDeleteConfirmDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    elevation: 8,
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ py: 2, bgcolor: 'error.main', color: 'white' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <WarningIcon sx={{ mr: 2 }} />
                        <Typography variant="h6" fontWeight="bold">
                            Confirm Delete
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 2 }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Are you sure you want to delete this tracking entry?
                        </Typography>
                        <Typography variant="body2">
                            This action cannot be undone. The tracking entry will be permanently removed from the shipment history.
                        </Typography>
                    </Alert>
                    
                    {deleteEntry && (
                        <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Entry Details:
                            </Typography>
                            <Typography variant="body2">
                                <strong>Activity:</strong> {deleteEntry.activity}<br/>
                                <strong>Date:</strong> {deleteEntry.date} at {deleteEntry.time}<br/>
                                <strong>Location:</strong> {deleteEntry.location}
                            </Typography>
                        </Paper>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
                    <Button 
                        onClick={() => {
                            setDeleteConfirmDialogOpen(false);
                            setDeleteEntry(null);
                        }}
                        variant="outlined"
                        size="large"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={confirmDeleteEntry}
                        variant="contained"
                        size="large"
                        color="error"
                        startIcon={<DeleteIcon />}
                    >
                        Delete Entry
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Image Viewer Modal */}
            <Dialog
                open={imageViewerOpen}
                onClose={handleCloseImageViewer}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: 'transparent',
                        boxShadow: 'none',
                        overflow: 'visible'
                    }
                }}
                BackdropProps={{
                    sx: {
                        bgcolor: 'rgba(0, 0, 0, 0.9)',
                        backdropFilter: 'blur(4px)'
                    }
                }}
            >
                <DialogContent sx={{ p: 0, position: 'relative', textAlign: 'center' }}>
                    {selectedImage && (
                        <>
                            <IconButton
                                onClick={handleCloseImageViewer}
                                sx={{
                                    position: 'absolute',
                                    top: 16,
                                    right: 16,
                                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                                    color: 'text.primary',
                                    zIndex: 1,
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 1)'
                                    }
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                            <img
                                src={selectedImage.url}
                                alt={selectedImage.alt}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '90vh',
                                    width: 'auto',
                                    height: 'auto',
                                    borderRadius: '8px',
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                                }}
                            />
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    color: 'white', 
                                    mt: 2, 
                                    textAlign: 'center',
                                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                                    px: 2,
                                    py: 1,
                                    borderRadius: 1,
                                    display: 'inline-block'
                                }}
                            >
                                {selectedImage.alt}
                            </Typography>
                        </>
                    )}
                </DialogContent>
            </Dialog>

        </DashboardLayout>
    );
};

export default TrackingManagement; 